import type { Dayjs } from 'dayjs';
import type { TimePickerProps } from '@mui/x-date-pickers';
import type { TextFieldProps } from '@mui/material/TextField';

import dayjs from 'dayjs';
import { Controller, useFormContext } from 'react-hook-form';

import { TimePicker } from '@mui/x-date-pickers';

import { formatPatterns } from 'src/utils/format-time';
import { ADMIN_TIME_ZONE, getDateInTimeZone } from 'src/utils/time-zone';

// ----------------------------------------------------------------------

type RHFTimePickerProps = TimePickerProps<Dayjs> & {
  name: string;
  valueAsString?: boolean;
};

export function RHFTimePicker({
  name,
  valueAsString,
  timezone,
  label,
  slotProps,
  ...other
}: RHFTimePickerProps) {
  const { control } = useFormContext();
  const pickerTimezone = valueAsString ? timezone : (timezone ?? ADMIN_TIME_ZONE);
  const pickerLabel =
    (timezone === undefined || timezone === ADMIN_TIME_ZONE) &&
    typeof label === 'string' &&
    !label.includes('(ET)')
      ? `${label} (ET)`
      : label;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TimePicker
          {...field}
          value={
            field.value === null || field.value === undefined
              ? null
              : pickerTimezone
                ? getDateInTimeZone(field.value, pickerTimezone)
                : dayjs(field.value)
          }
          onChange={(newValue) => {
            if (!newValue?.isValid()) {
              field.onChange(null);
            } else if (valueAsString) {
              field.onChange(newValue.toDate());
            } else {
              field.onChange(newValue.valueOf());
            }
          }}
          label={pickerLabel}
          timezone={pickerTimezone}
          format={formatPatterns.time}
          slotProps={{
            ...slotProps,
            textField: {
              fullWidth: true,
              error: !!error,
              helperText: error?.message ?? (slotProps?.textField as TextFieldProps)?.helperText,
              ...slotProps?.textField,
            },
          }}
          {...other}
        />
      )}
    />
  );
}
